/**
 * APT377: the dashboard opens the Accounts list with a filter in the URL, e.g. /home/accountclient?status=overdue.
 *
 *   status=overdue | due | submitted | approved   (sentForSignature is understood but the list has no such status yet)
 *   dueIn30=1                                       accounts due in the next 30 days
 *   outOfBalance=1 | locked=1                       clients with such a trial balance / locked period
 */

export type AccountsListTile = 'unbalanced' | 'locked';

export interface AccountsUrlFilters {
  /** The words the "Filtered by" chip shows, one per filter that is applied. */
  labels: string[];
  /** Accounts Status option id (the API's PeriodDefaults status), or null. */
  statusId: number | null;
  /** Due-date option the list already offers: 'overdue', 'notYetDue' or the API's DueRange 0 (due in 30 days). */
  due: 'overdue' | 'notYetDue' | 'in30' | null;
  /** The dashboard list that names the clients, for the filters the grid query has no parameter for. */
  tile: AccountsListTile | null;
}

/** Accounts Status dropdown ids the grid knows (see AccountsClient). */
const STATUS_APPROVED = 4;
const STATUS_SUBMITTED = 6;

export const NO_ACCOUNTS_FILTER: AccountsUrlFilters = { labels: [], statusId: null, due: null, tile: null };

export function parseAccountsFilters(params: URLSearchParams): AccountsUrlFilters {
  // A clients-named list (out of balance, locked) replaces the other filters: the grid query cannot combine them.
  if (params.get('outOfBalance') === '1') {
    return { labels: ['Out of balance'], statusId: null, due: null, tile: 'unbalanced' };
  }
  if (params.get('locked') === '1') {
    return { labels: ['Locked'], statusId: null, due: null, tile: 'locked' };
  }

  const labels: string[] = [];
  let statusId: number | null = null;
  let due: AccountsUrlFilters['due'] = null;

  switch (params.get('status')) {
    case 'overdue':
      due = 'overdue';
      labels.push('Overdue');
      break;
    case 'due':
      due = 'notYetDue';
      labels.push('Due');
      break;
    case 'submitted':
      statusId = STATUS_SUBMITTED;
      labels.push('Submitted');
      break;
    case 'approved':
      statusId = STATUS_APPROVED;
      labels.push('Approved');
      break;
    default:
      break;
  }

  if (params.get('dueIn30') === '1' && due === null) {
    due = 'in30';
    labels.push('Due in 30 days');
  }

  return { labels, statusId, due, tile: null };
}

/** The link the dashboard uses for a filter; `undefined` values are left out. */
export function accountsLink(filter: Record<string, string | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filter)) {
    if (value !== undefined) params.set(key, value);
  }
  const text = params.toString();
  return text ? `/home/accountclient?${text}` : '/home/accountclient';
}
